import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { PLAN_INTERVALS, SubscriptionInterval } from '../../../businessLogic/PlansData';
import { Color } from '../../../config/Color';
import { useAppStore } from '../../../store/Store';
import { Prices, PricingSelector } from '../../../store/pricing/PricingActions';
import { PurchaseSelector } from '../../../store/purchase/PurchaseActions';
import { PurchaseState } from '../../../store/purchase/PurchaseState';
import { Price } from '../../../util/Currency';
import { Line } from '../../styles/Dividers';
import { CostTableRows } from './CostComparisonExpTableRows';
import { SummaryTableRow } from './SummaryTableRow';

type PlanSummary = {
  purchase: PurchaseState;
  hasTrial: boolean;
  prices: Prices | undefined;
  finePrint: string | undefined;
  /** Set when the ongoing price is discounted from the regular one, e.g. by a forever coupon. */
  discountedBilling: { regular: Price; ongoing: Price } | undefined;
  planInterval: SubscriptionInterval | undefined;
};

/** The prices and fine print every version of the plan summary shows. */
export function usePlanSummary(): PlanSummary {
  const { purchase, hasTrial, hasOneTimeDiscount, planInterval, prices } = useAppStore((state) => ({
    purchase: state.purchase,
    hasTrial: PurchaseSelector.hasTrial(state.purchase),
    hasOneTimeDiscount: PricingSelector.hasOneTimeDiscount(state.pricing),
    planInterval: PLAN_INTERVALS[state.purchase.plan],
    prices: PricingSelector.prices(state),
  }));

  const { initialPlanPrice, ongoingPlanPrice } = prices ?? {};

  function getFinePrint(): string | undefined {
    if (!initialPlanPrice) {
      return undefined;
    }

    if (hasOneTimeDiscount) {
      return `Billed at ${initialPlanPrice} after ${purchase.trialPeriodDays} days
					then ${ongoingPlanPrice} every ${planInterval?.inText}`;
    }

    return `Billed at ${ongoingPlanPrice} every ${planInterval?.inText}`;
  }

  const discountedBilling =
    prices !== undefined && prices.regularPlanPrice.value !== prices.ongoingPlanPrice.value
      ? { regular: prices.regularPlanPrice, ongoing: prices.ongoingPlanPrice }
      : undefined;

  return { purchase, hasTrial, prices, finePrint: getFinePrint(), discountedBilling, planInterval };
}

export const PlanSummaryTableForm: FunctionComponent = () => {
  const { purchase, hasTrial, prices, finePrint, discountedBilling, planInterval } =
    usePlanSummary();
  const { trialPlanPrice, totalPriceToday } = prices ?? {};

  return (
    <SummaryTableContainer>
      <SummaryTableRow
        description="Total today"
        badge
        price={totalPriceToday?.toString()}
        bold
        dataTest="todayPrice"
      />
      {/* unsure if we need this hasTrial boolean anymore? */}
      {hasTrial && (
        <>
          <SummaryTableRow
            dataTest="trialPrice"
            description={`${purchase.trialPeriodDays}-day ${PurchaseSelector.hasFreeTrial(purchase) ? 'free' : ''} trial`}
            price={trialPlanPrice?.toString()}
          />
          <Line color={Color.content.default} />
          <CostTableRows
            prices={prices}
            finePrint={finePrint}
            discountedBilling={discountedBilling}
            planIntervalText={planInterval?.inText}
          />
        </>
      )}
    </SummaryTableContainer>
  );
};

export const SummaryTableContainer = styled.div`
  border: 1px solid ${Color.decorative.default};
  border-radius: 8px;
  padding: ${({ theme }) => `${theme.spacing._24px}`};
  text-align: left;
`;
